'use client';

import type { ReactNode } from 'react';
import { ChevronRight, Phone, Siren, Users } from 'lucide-react';
import { Avatar as BaseAvatar, AvatarStack as BaseAvatarStack } from '../Avatar';

/**
 * The profile's people rows: a face with what someone is to this person,
 * the emergency contact, the medical chips, a group with its faces. Pure
 * drawing; every tap is the caller's.
 */

/** A face, marked personal (`.pii`) so a demo can blur it. */
export function Avatar({
  name,
  imageUrl,
  size,
}: {
  name: string;
  imageUrl?: string | null;
  size?: number;
}) {
  return (
    <span className="pii inline-flex shrink-0 rounded-full align-middle">
      <BaseAvatar name={name} imageUrl={imageUrl ?? null} size={size} />
    </span>
  );
}

export function AvatarStack({
  people,
  size,
}: {
  people: { name: string; imageUrl?: string | null }[];
  size?: number;
}) {
  return (
    <span className="pii inline-flex shrink-0 align-middle">
      <BaseAvatarStack people={people} size={size} />
    </span>
  );
}

/**
 * Someone in the household and what they are to this person ("Rachel ·
 * Mother"), or `Not set` in teal as an invitation. The whole row is the tap.
 */
export function RelationRow({
  name,
  label,
  imageUrl,
  relation,
  onClick,
}: {
  /** For the face's initials */
  name: string;
  /** What the row says (defaults to `name`): "You", a first name */
  label?: ReactNode;
  imageUrl: string | null;
  /** Null reads "Not set" */
  relation: ReactNode | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="flex w-full items-center gap-3 rounded-2xl px-2 py-1.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      <Avatar name={name} imageUrl={imageUrl} size={32} />
      <span className="pii min-w-0 flex-1 truncate text-sm font-semibold text-[#1c2b39]">
        {label ?? name}
      </span>
      <span
        className={`shrink-0 text-sm ${relation ? 'text-gray-600' : 'font-semibold text-[#299c8f]'}`}
      >
        {relation ?? 'Not set'}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" strokeWidth={2.5} />
    </button>
  );
}

/** Relatives outside the household, as a small stack of faces and one line. */
export function ElsewhereLine({
  people,
  children,
}: {
  people: { key: string | number; name: string; imageUrl: string | null }[];
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
      <span className="flex shrink-0 -space-x-2">
        {people.slice(0, 4).map((p) => (
          <span key={p.key} className="rounded-full ring-2 ring-white">
            <Avatar name={p.name} imageUrl={p.imageUrl} size={28} />
          </span>
        ))}
      </span>
      <span className="pii min-w-0 flex-1 truncate text-xs text-gray-500">{children}</span>
    </div>
  );
}

/**
 * Who to call first, as the Medical card's top row: their face (or a phone
 * for a name typed in, or a red siren when there is nobody), the name and
 * number, a chevron. The whole row is the tap.
 */
export function EmergencyContactRow({
  contact,
  detail,
  prompt,
  onClick,
}: {
  /** `person` is someone in MP (a face); `external` a name typed in */
  contact: { kind: 'person' | 'external'; name: string; imageUrl: string | null } | null;
  /** The line under the name: phone · relationship */
  detail?: ReactNode;
  /** Said in teal when there is nobody ("Who should we call about Sam?") */
  prompt: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="-mx-2 flex items-center gap-3 rounded-2xl px-2 py-1.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      {contact ? (
        contact.kind === 'person' ? (
          <Avatar name={contact.name} imageUrl={contact.imageUrl} size={40} />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
            <Phone className="h-4 w-4" strokeWidth={2.5} />
          </span>
        )
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
          <Siren className="h-4 w-4" strokeWidth={2.5} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-bold tracking-widest text-gray-400 uppercase">
          Emergency contact
        </span>
        {contact ? (
          <>
            <span className="pii block truncate text-sm font-bold text-[#1c2b39]">
              {contact.name}
            </span>
            <span className="pii block truncate text-xs text-gray-500">
              {detail || 'No phone on file'}
            </span>
          </>
        ) : (
          <span className="block text-sm font-semibold text-[#299c8f]">{prompt}</span>
        )}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" strokeWidth={2.5} />
    </button>
  );
}

/** What is on someone's medical card, as chips; severe or expired ones in red. */
export function MedicalChips({
  items,
}: {
  items: {
    id: number;
    label: string;
    /** Shown on hover: "Allergy", "Medication" */
    kindLabel: string;
    severe: boolean;
    /** Null when it does not expire */
    expires: { expired: boolean; label: string } | null;
  }[];
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((m) => (
        <span
          key={m.id}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
            m.severe || m.expires?.expired
              ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
              : 'bg-gray-100 text-[#1c2b39]'
          }`}
          title={m.kindLabel}
        >
          {m.label}
          {m.expires && (
            <span className="font-normal opacity-70">
              · {m.expires.expired ? 'expired' : `to ${m.expires.label}`}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/** A group as one row: its faces, its name, and a pill saying where they stand ("You lead"). */
export function GroupRow({
  name,
  members,
  badge,
  leader,
  href,
}: {
  name: string;
  members: { name: string; imageUrl?: string | null }[];
  badge: string;
  /** The pill goes teal */
  leader: boolean;
  /** Makes the row a link (opens in a new tab) */
  href?: string;
}) {
  const inner = (
    <>
      {members.length > 0 ? (
        <AvatarStack people={members} size={28} />
      ) : (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
          <Users className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1c2b39]">{name}</span>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase ${
          leader ? 'bg-[#299c8f] text-white' : 'bg-gray-100 text-gray-600'
        }`}
      >
        {badge}
      </span>
    </>
  );
  const cls = 'flex items-center gap-3 px-2 py-2.5';
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`${cls} rounded-2xl transition-colors hover:bg-gray-50`}
    >
      {inner}
    </a>
  ) : (
    <div className={cls}>{inner}</div>
  );
}
